<template>
  <div
    ref="refContainerScroll"
    :class="[
      'auto-scroll-container',
      {
        'instant-scroll': !isScrollInstant,
        'no-scroll-bar': !hasScrollBar,
        'no-user-scroll': !hasUserScroll,
      },
    ]"
    @scroll="handleScroll"
  >
    <div ref="refContentScroll" :style="{ 'min-height': maxContentHeight + 'px' }">
      <slot />
      <div v-if="brickHeight" ref="refScrollBrick" :style="brickStyle" />
    </div>

    <div ref="refButtonWrap" class="button-wrap">
      <ButtonScrollDown v-if="hasButtonScroll && !isActiveAutoScroll" @scroll-to-bottom="scrollToBottom" />
      <slot name="buttons" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMutationObserver } from '@vueuse/core';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  readonly,
  useTemplateRef,
  CSSProperties,
} from 'vue';

import { debounce } from 'lodash';

import ButtonScrollDown from '@/components/shared/base/ButtonScrollDown.vue';
import { useAudioStore } from '@/stores/audio';
import { useUserStore } from '@/stores/user';

const props = withDefaults(
  defineProps<{
    hasButtonScroll?: boolean;
    hasScrollBar?: boolean;
    hasUserScroll?: boolean;
    isNonStop?: boolean;
    brickHeight?: string;
  }>(),
  {
    hasButtonScroll: false,
    hasScrollBar: true,
    hasUserScroll: true,
    isNonStop: false,
    brickHeight: '',
  }
);

const userStore = useUserStore();
const audioStore = useAudioStore();

const refContainerScroll = ref<HTMLDivElement | null>(null); // 스크롤 가능한 DOM (높이 제한 있음)
const refContentScroll = ref<HTMLDivElement | null>(null); // 내용을 담고 있는 DOM (높이 제한 없음)
const refScrollBrick = ref<HTMLDivElement | null>(null); // 하단 벽돌

const refButtonWrap = useTemplateRef('refButtonWrap');

const _isActiveAutoScroll = ref(true); // 자동 스크롤 활성화 여부
const _isScrollInstant = ref(false); // 즉각 스크롤 여부
const _maxContentHeight = ref(0); // 내용의 최대 높이

const isActiveAutoScroll = readonly(_isActiveAutoScroll); // 자동 스크롤 활성화 여부
const isScrollInstant = readonly(_isScrollInstant); // 즉각 스크롤 여부
const maxContentHeight = readonly(_maxContentHeight); // 내용의 최대 높이

let resizeObserver: ResizeObserver | null = null; // refContent가 커졌는지 감시하는 옵저버
let intersectionObserver: IntersectionObserver | null = null; // 벽돌이 보이는지 감시하는 옵저버
let scrollTopLast = 0;
let scrollHeightLast = 0;

// 벽돌 스타일
const brickStyle = computed<CSSProperties>(() => {
  return {
    opacity: 0,
    'min-height': props.brickHeight,
  };
});

const setIsActiveAutoScroll = (newVal) => {
  _isActiveAutoScroll.value = newVal;
};

const setIsScrollInstant = (newVal) => {
  _isScrollInstant.value = newVal;
};

const setMaxContentHeight = (newVal) => {
  _maxContentHeight.value = newVal;
};

// 맨 아래로 스크롤
const scrollToBottom = () => {
  const el = refContainerScroll.value;
  if (!el) return;

  requestAnimationFrame(() => {
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  });

  setAutoScrollOn();
};

// 높이 변경 옵저버 초기화
const setupObserverResize = () => {
  const elementTarget = refContentScroll.value;
  if (!elementTarget) return;

  resizeObserver = new ResizeObserver((entries) => {
    const entry = entries[0];

    if (!entry) return;
    if (entry.contentRect.height === scrollHeightLast) return;

    if (entry.contentRect.height > maxContentHeight.value) {
      setMaxContentHeight(entries[0].contentRect.height);
    }

    scrollHeightLast = entry.contentRect.height;

    if (!isActiveAutoScroll.value || props.brickHeight) return;

    scrollToBottom();
  });

  resizeObserver.observe(elementTarget);
  scrollToBottom();
};

// 벽돌 인터섹션 옵저버 초기화
const setupObserverIntersection = () => {
  const brick = refScrollBrick.value;
  const root = refContainerScroll.value;

  if (!brick || !root) return;

  if (intersectionObserver) {
    intersectionObserver.disconnect();
  }

  intersectionObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry) return;

      const ratio = entry.intersectionRatio;

      if (ratio <= 0.1 && isActiveAutoScroll.value) {
        scrollToBottom();
      }
    },
    {
      root,
      threshold: [0, 0.1],
    }
  );

  intersectionObserver.observe(brick);
};

// 스크롤 발생 시
const handleScroll = () => {
  requestAnimationFrame(() => {
    const el = refContainerScroll.value;
    if (!el) return;

    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 20;

    if (el.scrollTop < scrollTopLast) {
      scrollTopLast = el.scrollTop;

      if (el.scrollHeight < scrollHeightLast) {
        scrollHeightLast = el.scrollHeight;

        debounce(() => {
          handleScroll();
        }, 200);

        return;
      }

      if (!isNearBottom) setAutoScrollOff();
      return;
    }

    scrollTopLast = el.scrollTop;

    if (isNearBottom) setAutoScrollOn();
  });
};

// 자동 스크롤 켜기
const setAutoScrollOn = () => {
  setIsActiveAutoScroll(true);
};

// 자동 스크롤 끄기
const setAutoScrollOff = () => {
  if (props.isNonStop) return;
  setIsActiveAutoScroll(false);
};

// 컨테이너 최소 크기 초기화 (여백 방지)
const clearMinHeight = () => {
  setMaxContentHeight(0);
};

const handleResizeScreen = () => {
  if (isActiveAutoScroll.value) {
    scrollToBottom();
  }

  clearMinHeight();
};

onMounted(async () => {
  await nextTick();
  setupObserverResize();
  updateButtonWrapHeight();

  // 스크린 모드
  if (props.brickHeight) {
    setupObserverIntersection();
    window.addEventListener('resize', handleResizeScreen);
    // 일반 통역
  } else {
    window.addEventListener('resize', clearMinHeight);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver && refContentScroll.value) {
    resizeObserver.unobserve(refContentScroll.value);
    resizeObserver.disconnect();
  }

  if (intersectionObserver) {
    intersectionObserver.disconnect();
    intersectionObserver = null;
  }

  if (props.brickHeight) {
    window.removeEventListener('resize', handleResizeScreen);
  } else {
    window.removeEventListener('resize', clearMinHeight);
  }
});

// 글자 크기, 단일언어 값 변경 시 minHeight 초기화 (여백 방지)
watch(
  [() => userStore.userPreferences, () => audioStore.isSingleLanguage],
  () => {
    setMaxContentHeight(0);
  },
  { deep: true }
);

const updateButtonWrapHeight = () => {
  if (refButtonWrap.value) {
    document.documentElement.style.setProperty('--bt', `${refButtonWrap.value.offsetHeight}px`);
  }
};

useMutationObserver(refButtonWrap, updateButtonWrapHeight, { childList: true });

// 부모에게 노출할 속성 및 메서드
defineExpose({
  refContainerScroll,
  refContentScroll,
  isActiveAutoScroll,
  maxContentHeight,
  scrollToBottom,
  setAutoScrollOn,
  setAutoScrollOff,
  setIsScrollInstant,
  setMaxContentHeight,
});
</script>

<style lang="scss" scoped>
$visual-height: var(--vh, 100dvh);
$button-height: var(--bt, 100dvh);

.auto-scroll-container {
  position: relative;
  overflow-y: auto;
  scroll-behavior: smooth;
  height: 100%;
  min-height: 0;
  overflow-anchor: none;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding-bottom: env(safe-area-inset-bottom, 3.5rem);
  top: env(safe-area-inset-top, 8px);
}

.instant-scroll {
  scroll-behavior: auto !important;
}

.no-scroll-bar {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.no-user-scroll {
  pointer-events: none;
}

.button-wrap {
  z-index: 1;
  position: fixed;
  right: 0;
  top: calc($visual-height - $button-height - 8rem);
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 0.5rem;
  pointer-events: none;
  margin-top: auto;

  & > * {
    pointer-events: auto;
  }
}
</style>
